/* @flow @format */
import { type ContextRouter, type Location, type Match, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { defaultMemoize, createSelector } from 'reselect';
import { matchRoutes, renderRoutes } from 'react-router-config';
import F from 'lodash/fp';
import * as React from 'react';

import { type Contest, fetchAllContestsBegin, fetchContestBegin } from '../redux/reducer/entities';
import type { ContestType, Dispatch, StoreState } from '../redux/types';
import {
  contest,
  selectAllMissionCoursesForContest,
  selectContestByType,
  selectIsContestLoading,
  selectIsContestStale,
  selectIsRankingLoading,
} from '../redux/selector';
import { fetchRankingsBegin } from '../redux/reducer/ranking';
import { fetchUserConfigBegin } from '../redux/reducer/config';
import ContestIndexPage from './ContestIndexPage';
import CoursePage from './common/CoursePage';
import HomePage from './HomePage';
import IceAdvancedFieldPage from './ice-a/fields/IceAdvancedFieldPage';
import IceAdvancedRankingPage from './ice-a/fields/IceAdvancedRankingPage';
import IceFieldAdvanceChallengePage from './ice-a/fields/IceFieldAdvanceChallengePage';
import IceFieldChallengePage from './ice/fields/IceFieldChallengePage';
import IceFieldPage from './ice/fields/IceFieldPage';
import IceFieldResearchPage from './ice/fields/IceFieldResearchPage';
import IceRankingPage from './ice/fields/IceRankingPage';
import MissionPage from './common/MissionPage';
import PizzaAdvanceFieldChallengePage from './pizza-a/fields/PizzaAdvanceFieldChallengePage';
import PizzaAdvancedFieldPage from './pizza-a/fields/PizzaAdvancedFieldPage';
import PizzaAdvancedRankingPage from './pizza-a/fields/PizzaAdvancedRankingPage';
import PizzaFieldChallengePage from './pizza/fields/PizzaFieldChallengePage';
import PizzaFieldPage from './pizza/fields/PizzaFieldPage';
import PizzaFieldResearchPage from './pizza/fields/PizzaFieldResearchPage';
import PizzaRankingPage from './pizza/fields/PizzaRankingPage';
import ProcSample from './ProcSample';
import ProcSample1 from './ProcSample1';
import ProcSample2 from './ProcSample2';
import SplashPage from './SplashPage';
import SushiAdvanceFieldChallengePage from './sushi-a/fields/SushiAdvanceFieldChallengePage';
import SushiAdvancedFieldPage from './sushi-a/fields/SushiAdvancedFieldPage';
import SushiAdvancedRankingPage from './sushi-a/fields/SushiAdvancedRankingPage';
import SushiFieldChallengePage from './sushi/fields/SushiFieldChallengePage';
import SushiFieldPage from './sushi/fields/SushiFieldPage';
import SushiRankingPage from './sushi/fields/SushiRankingPage';
import WelcomePage from './common/WelcomePage';
import ZKaiExternalDestinationList from './ZkaiExternalDestinationList';
import ZKaiPrivacy from './ZkaiPrivacy';
import ZKaiPrivacyOld from './ZkaiPrivacyOld';
import ZPro1 from './ZPro1';
import ZPro2 from './ZPro2';
import ZPro3 from './ZPro3';
import ZPro4 from './ZPro4';
import ZPro5 from './ZPro5';
import ZPro6 from './ZPro6';
import ZkaiTerm from './ZkaiTerm';
import ZkaiTermOld from './ZkaiTermOld';

const preloadIndex = async (dispatch: Dispatch) => {
  dispatch(fetchAllContestsBegin());
  dispatch(fetchUserConfigBegin());
};

const preloadRankings = (kind: ContestType) => async (dispatch: Dispatch, store: StoreState) => {
  const uid = contest[kind].fieldUid(store);
  if (!uid) throw new Error('Unknown uid');
  dispatch(fetchRankingsBegin({ uid }));
};

const preloadField = (kind: ContestType) => async (_: Dispatch, store: StoreState) => {
  const courseContentUid = contest[kind].fieldUid(store);
  if (!courseContentUid) throw new Error('Unknow uid');
};

const CONTEST_TYPE_PARAM = ':contestType(ice|pizza|sushi|ice-a|pizza-a|sushi-a)';
const COURSE_UID_PARAM = ':courseUid';

async function preloadContestIndex(dispatch: Dispatch, store: StoreState, match: Match) {
  const contestType: any = match.params.contestType;
  const contestUid = contest[contestType].contestUid(store);
  if (!contestUid) throw new TypeError(`There are no ${contestType} contest`);
  const needFetch = selectIsContestStale(store, contestUid);
  if (needFetch) dispatch(fetchContestBegin(contestUid));
}

function redirectTo404() {
  window.location = 'https://proc-error.sonyged.com/404.html';
}

type StaticRouterProps = {|
  ...ContextRouter,
  route: any,
|};

type ContestGroupProps = {|
  ...StaticRouterProps,
  contest: Contest,
  contestType: ContestType,
|};

const ContestGroup = ({ route, contest, contestType }: ContestGroupProps) => (
  <React.Fragment>{renderRoutes(route.routes, { contest, contestType })}</React.Fragment>
);

// $FlowFixMe
const ContestGroupContainer = connect((state, op: StaticRouterProps) => {
  const contestType = op.match.params.contestType;
  const contest = selectContestByType(state, { contestType });
  return { contest, contestType };
})(ContestGroup);

type CourseGroupProps = {|
  ...ContestGroupProps,
  course: any,
|};

const CourseGroup = ({ route, contest, contestType, course }: CourseGroupProps) => (
  <React.Fragment>{renderRoutes(route.routes, { contest, contestType, course })}</React.Fragment>
);

// $FlowFixMe
const CourseGroupContainer = connect((state, { match, contest }: ContestGroupProps) => {
  const courseUid = match.params.courseUid;
  const courses = selectAllMissionCoursesForContest(state, contest);

  let course = null;
  if (courseUid) course = F.find((course) => course.uid === courseUid, courses);
  else course = F.find((course) => course.isDefault, courses);

  if (!course) redirectTo404();

  return { course };
})(CourseGroup);

const e404Route = {
  key: '404',
  component: () => {
    window.location = 'https://proc-error.sonyged.com/404.html';
    return null;
  },
};

export const routes = [
  {
    key: 'index',
    exact: true,
    path: '/',
    component: HomePage,
    preload: preloadIndex,
    loadingSelector: () => selectIsContestLoading,
  },
  {
    path: `/${CONTEST_TYPE_PARAM}`,
    component: ContestGroupContainer,
    preload: preloadContestIndex,
    loadingSelector: () => selectIsContestLoading,
    routes: [
      {
        path: `/${CONTEST_TYPE_PARAM}/welcome`,
        exact: true,
        component: WelcomePage,
      },
      {
        path: `/${CONTEST_TYPE_PARAM}`,
        exact: true,
        component: ContestIndexPage,
      },
      {
        path: [`/${CONTEST_TYPE_PARAM}/courses/missions`, `/${CONTEST_TYPE_PARAM}/courses/${COURSE_UID_PARAM}?`],
        component: CourseGroupContainer,
        routes: [
          {
            path: `/${CONTEST_TYPE_PARAM}/courses/${COURSE_UID_PARAM}?`,
            exact: true,
            component: CoursePage,
          },
          {
            path: [
              `/${CONTEST_TYPE_PARAM}/courses/missions/:id`,
              `/${CONTEST_TYPE_PARAM}/courses/${COURSE_UID_PARAM}/missions/:id`,
            ],
            exact: true,
            component: MissionPage,
          },
          e404Route,
        ],
      },
      {
        path: `/ice/fields`,
        exact: true,
        component: IceFieldPage,
        preload: preloadField('ice'),
      },
      {
        key: 'ice/fields/research',
        exact: true,
        path: '/ice/fields/research',
        component: IceFieldResearchPage,
        preload: preloadField('ice'),
      },
      {
        key: 'ice/fields/challenge',
        exact: true,
        path: '/ice/fields/challenge',
        component: IceFieldChallengePage,
        preload: preloadField('ice'),
      },
      {
        key: 'ice/fields/rank',
        exact: true,
        path: '/ice/fields/rank',
        component: IceRankingPage,
        preload: preloadRankings('ice'),
        loadingSelector: () => selectIsRankingLoading,
      },
      {
        key: 'pizza/fields',
        exact: true,
        path: '/pizza/fields',
        component: PizzaFieldPage,
        preload: preloadField('pizza'),
      },
      {
        key: 'pizza/fields/challenge',
        exact: true,
        path: '/pizza/fields/challenge',
        component: PizzaFieldChallengePage,
        preload: preloadField('pizza'),
      },
      {
        key: 'pizza/fields/research',
        exact: true,
        path: '/pizza/fields/research',
        component: PizzaFieldResearchPage,
        preload: preloadField('pizza'),
      },
      {
        key: 'pizza/fields/rank',
        exact: true,
        path: '/pizza/fields/rank',
        component: PizzaRankingPage,
        preload: preloadRankings('pizza'),
        loadingSelector: () => selectIsRankingLoading,
      },

      {
        key: 'sushi/fields',
        exact: true,
        path: '/sushi/fields',
        component: SushiFieldPage,
        preload: preloadField('sushi'),
      },
      {
        key: 'sushi/fields/challenge',
        exact: true,
        path: '/sushi/fields/challenge',
        component: SushiFieldChallengePage,
        preload: preloadField('sushi'),
      },
      {
        key: 'sushi/fields/rank',
        exact: true,
        path: '/sushi/fields/rank',
        component: SushiRankingPage,
        preload: preloadRankings('sushi'),
        loadingSelector: () => selectIsRankingLoading,
      },
      {
        key: 'ice-a/fields',
        exact: true,
        path: '/ice-a/fields',
        component: IceAdvancedFieldPage,
        preload: preloadField('ice-a'),
      },
      {
        key: 'ice-a/fields/challenge',
        exact: true,
        path: '/ice-a/fields/challenge',
        component: IceFieldAdvanceChallengePage,
        preload: preloadField('ice-a'),
      },
      {
        key: 'ice-a/fields/rank',
        exact: true,
        path: '/ice-a/fields/rank',
        component: IceAdvancedRankingPage,
        preload: preloadRankings('ice-a'),
        loadingSelector: () => selectIsRankingLoading,
      },
      {
        key: 'pizza-a/fields',
        exact: true,
        path: '/pizza-a/fields',
        component: PizzaAdvancedFieldPage,
        preload: preloadField('pizza-a'),
      },
      {
        key: 'pizza-a/fields/challenge',
        exact: true,
        path: '/pizza-a/fields/challenge',
        component: PizzaAdvanceFieldChallengePage,
        preload: preloadField('pizza-a'),
      },
      {
        key: 'pizza-a/fields/rank',
        exact: true,
        path: '/pizza-a/fields/rank',
        component: PizzaAdvancedRankingPage,
        preload: preloadRankings('pizza-a'),
        loadingSelector: () => selectIsRankingLoading,
      },
      {
        key: 'sushi-a/fields',
        exact: true,
        path: '/sushi-a/fields',
        component: SushiAdvancedFieldPage,
        preload: preloadField('sushi-a'),
      },
      {
        key: 'sushi-a/fields/challenge',
        exact: true,
        path: '/sushi-a/fields/challenge',
        component: SushiAdvanceFieldChallengePage,
        preload: preloadField('sushi-a'),
      },
      {
        key: 'sushi-a/fields/rank',
        exact: true,
        path: '/sushi-a/fields/rank',
        component: SushiAdvancedRankingPage,
        preload: preloadRankings('sushi-a'),
        loadingSelector: () => selectIsRankingLoading,
      },

      e404Route,
    ],
  },

  {
    key: 'zpro1',
    exact: true,
    path: '/z-pro-1',
    component: ZPro1,
  },
  {
    key: 'zpro2',
    exact: true,
    path: '/z-pro-2',
    component: ZPro2,
  },
  {
    key: 'zpro3',
    exact: true,
    path: '/z-pro-3',
    component: ZPro3,
  },
  {
    key: 'zpro4',
    exact: true,
    path: '/z-pro-4',
    component: ZPro4,
  },
  {
    key: 'zpro5',
    exact: true,
    path: '/z-pro-5',
    component: ZPro5,
  },
  {
    key: 'zpro6',
    exact: true,
    path: '/z-pro-6',
    component: ZPro6,
  },
  { key: 'z-pro-privacy', exact: true, path: '/z-pro-privacy', component: ZKaiPrivacy },
  { key: 'z-pro-privacy-old', exact: true, path: '/z-pro-privacy-old', component: ZKaiPrivacyOld },
  { key: 'z-pro-term', exact: true, path: '/z-pro-term', component: ZkaiTerm },
  { key: 'z-pro-term-old', exact: true, path: '/z-pro-term-old', component: ZkaiTermOld },
  { key: 'z-pro-external-destination-list', exact: true, path: '/z-pro-external-destination-list', component: ZKaiExternalDestinationList },
  e404Route,
];

declare var __PRODUCTION__: boolean;
if (!__PRODUCTION__) {
  // add routes for sample page

  // last route is 404 route
  const e404 = routes.pop();

  routes.push(
    {
      key: 'sample1',
      exact: true,
      path: '/proc-sample-1',
      component: ProcSample1,
    },
    {
      key: 'sample2',
      exact: true,
      path: '/proc-sample-2',
      component: ProcSample2,
    },
    {
      key: 'other',
      path: '/:slug',
      component: ProcSample,
    },
    e404
  );
}

type Props = {
  children: React.Node,
  location: Location,
  store: StoreState,
  dispatch: Dispatch,
};

type State = {
  location: Location,
  previousLocation: ?Location,
  loadingSelector: (StoreState) => boolean,
};

const preloadData = (dispatch: Dispatch, store: StoreState, location: Location) => {
  const branch = matchRoutes(routes, location.pathname);
  const promises = branch.map(({ route, match }) =>
    route.preload ? route.preload(dispatch, store, match) : Promise.resolve()
  );
  return Promise.all(promises);
};

const loadingConst = () => true;
const loadingFinishedConst = () => false;

const loadingSelectors = defaultMemoize((location: Location) => {
  const branch = matchRoutes(routes, location.pathname);
  return branch.map(({ route, match }) =>
    route.loadingSelector ? route.loadingSelector(match) : loadingFinishedConst
  );
});

class PrefetcherInner extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      location: props.location,
      previousLocation: null,
      loadingSelector: loadingConst,
    };
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const navigated = nextProps.location.pathname !== prevState.location.pathname;
    if (navigated) {
      const loading = prevState.loadingSelector(nextProps.store);
      return {
        location: nextProps.location,
        previousLocation: loading ? prevState.previousLocation : prevState.location,
        loadingSelector: loadingConst,
      };
    }
    return null;
  }

  afterUpdate = (prevProps: ?Props) => {
    const { location, dispatch, store } = this.props;
    const prevLocation = prevProps && prevProps.location;
    if (prevLocation !== location) {
      preloadData(dispatch, store, location)
        .then(() => {
          if (location.pathname !== this.props.location.pathname) {
            // user had clicked another link, abort current;
            return;
          }
          const selectors = loadingSelectors(location);
          const loadingSelector = createSelector(selectors, (...loadings) => loadings.reduce((a, b) => a || b));

          this.setState({ loadingSelector });
        })
        .catch((_e) => {
          console.info({ _e });
          window.location = 'https://proc-error.sonyged.com/404.html';
          return;
        });
    }
  };

  componentDidMount() {
    this.afterUpdate();
  }

  componentDidUpdate(prevProps) {
    this.afterUpdate(prevProps);
  }

  render() {
    const { children, location, store } = this.props;
    const { previousLocation, loadingSelector } = this.state;

    let renderer = () => children;
    let currentLocation = location;

    const loading = loadingSelector(store);
    if (loading) {
      if (previousLocation) {
        currentLocation = previousLocation;
      } else {
        renderer = () => <SplashPage />;
      }
    }

    // use a controlled <Route> to trick all descendants into
    // rendering the old location
    // $FlowFixMe
    return <Route location={currentLocation} render={renderer} />;
  }
}

export const Prefetcher = withRouter(connect((store) => ({ store }))(PrefetcherInner));
